<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter, type LocationQueryRaw } from 'vue-router'
import { ChartLine } from 'lucide-vue-next'
import { insightsApi } from '@/api/client'
import { Skeleton } from '@/components/ui/skeleton'
import InsightsFilterBar from '@/components/InsightsFilterBar.vue'
import InsightsSegmented from '@/components/InsightsSegmented.vue'
import InsightsModelView from '@/components/InsightsModelView.vue'
import InsightsCompetitorView from '@/components/InsightsCompetitorView.vue'
import InsightsInfoTip from '@/components/InsightsInfoTip.vue'
import type { InsightsFilterOptions, InsightsRange, InsightsScope, InsightsSummary, InsightsView } from '@/lib/insights'
import { INSIGHTS_RANGES, INSIGHTS_SIDES, formatCount, scopeQuery } from '@/lib/insights'
import dayjs from 'dayjs'

/**
 * The customer-facing Insights page (REC-854).
 *
 * The URL is the state: scope, view and range live in the query, so a reload or a
 * shared link reopens the same view. `/filters` answers which values are valid for
 * the levels above; a level the answer doesn't contain (a stale link, a line that
 * isn't in the newly picked region) is corrected in the URL, one level per round
 * trip, and the data views only load once the whole scope is valid.
 */
const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()

const SCOPE_KEYS = ['geo', 'brand_id', 'line', 'tier', 'storage', 'grade'] as const

const queryValue = (key: string): string | null => {
    const value = route.query[key]

    return typeof value === 'string' && value !== '' ? value : null
}

const requested = computed(() => ({
    geo: queryValue('geo'),
    brand_id: queryValue('brand_id'),
    line: queryValue('line'),
    tier: queryValue('tier'),
    storage: queryValue('storage'),
    grade: queryValue('grade'),
}))

const view = computed<InsightsView>(() => (queryValue('view') === 'competitor' ? 'competitor' : 'model'))

const range = computed<InsightsRange>(() => {
    const value = queryValue('range')

    return INSIGHTS_RANGES.find((candidate) => candidate === value) ?? '1y'
})

/** The levels `/filters` takes; storage and grade options follow from them. */
const filtersKey = computed(() =>
    JSON.stringify([requested.value.geo, requested.value.brand_id, requested.value.line, requested.value.tier]),
)

const options = ref<InsightsFilterOptions | null>(null)
const optionsKey = ref<string | null>(null)
const loadingFilters = ref(false)
const failed = ref(false)
let filtersRequest = 0

const updateQuery = (patch: LocationQueryRaw) => {
    const query: LocationQueryRaw = { ...route.query, ...patch }

    for (const key of Object.keys(query)) {
        if (query[key] === null || query[key] === undefined) {
            delete query[key]
        }
    }

    return router.replace({ query })
}

/**
 * The first level of the request the options don't support, with what to put there
 * instead; null once every level is valid.
 */
const correction = (data: InsightsFilterOptions): LocationQueryRaw | null => {
    const wanted = requested.value
    const geos = data.regions.flatMap((region) => [region.geo, ...region.countries.map((country) => country.geo)])

    if (data.regions.length === 0) {
        return null
    }

    if (wanted.geo === null || !geos.includes(wanted.geo)) {
        return { geo: data.regions[0].geo }
    }

    if (data.brands.length === 0) {
        return null
    }

    if (wanted.brand_id === null || !data.brands.some((brand) => String(brand.id) === wanted.brand_id)) {
        return { brand_id: String(data.brands[0].id), line: null, tier: null, storage: null, grade: null }
    }

    if (wanted.line !== null && !data.lines.some((line) => line.code === wanted.line)) {
        return { line: null, tier: null }
    }

    if (wanted.tier !== null && (wanted.line === null || !data.tiers.some((tier) => tier.code === wanted.tier))) {
        return { tier: null }
    }

    if (wanted.storage !== null && !data.storages.some((storage) => storage.code === wanted.storage)) {
        return { storage: null }
    }

    if (wanted.grade !== null && !data.grades.some((grade) => grade.code === wanted.grade)) {
        return { grade: null }
    }

    return null
}

const loadFilters = async () => {
    const token = ++filtersRequest
    const key = filtersKey.value
    const params = new URLSearchParams()

    for (const level of ['geo', 'brand_id', 'line', 'tier'] as const) {
        const value = requested.value[level]

        if (value !== null) {
            params.set(level, value)
        }
    }

    loadingFilters.value = true
    failed.value = false

    try {
        const { data } = await insightsApi.filters(params.toString())

        if (token !== filtersRequest) {
            return
        }

        const fix = data.week === null ? null : correction(data)

        if (fix !== null) {
            await updateQuery(fix)

            return
        }

        options.value = data
        optionsKey.value = key
    } catch {
        if (token === filtersRequest) {
            failed.value = true
        }
    } finally {
        if (token === filtersRequest) {
            loadingFilters.value = false
        }
    }
}

watch(filtersKey, loadFilters, { immediate: true })

/** Storage and grade are checked here too: a stale link can carry one the scope lacks. */
watch(
    () => [requested.value.storage, requested.value.grade],
    () => {
        if (options.value === null || optionsKey.value !== filtersKey.value) {
            return
        }

        const fix = correction(options.value)

        if (fix !== null) {
            updateQuery(fix)
        }
    },
)

/** The scope the data views load, or null while the URL is still being validated. */
const scope = computed<InsightsScope | null>(() => {
    const wanted = requested.value

    if (options.value === null || optionsKey.value !== filtersKey.value) {
        return null
    }

    if (wanted.geo === null || wanted.brand_id === null || correction(options.value) !== null) {
        return null
    }

    return {
        geo: wanted.geo,
        brand_id: Number(wanted.brand_id),
        line: wanted.line,
        tier: wanted.tier,
        storage: wanted.storage,
        grade: wanted.grade,
    }
})

const hasNoData = computed(
    () => options.value !== null && (options.value.week === null || options.value.regions.length === 0),
)

/**
 * A pick resets what depends on it: a new brand has other lines, a new line other
 * tiers. Everything else is kept and corrected by `/filters` if the new scope lacks it.
 */
const onFilterChange = (level: keyof InsightsScope, value: string | null) => {
    const patch: LocationQueryRaw = { [level]: value }

    if (level === 'brand_id') {
        Object.assign(patch, { line: null, tier: null, storage: null, grade: null })
    }

    if (level === 'line') {
        patch.tier = null
    }

    updateQuery(patch)
}

const viewOptions = computed(() => [
    { value: 'model' as const, label: t('insights.views.model') },
    { value: 'competitor' as const, label: t('insights.views.competitor') },
])

/** "EU · Italy · Apple · iPhone 13": the scope in words, under the title. */
const scopeTitle = computed(() => {
    if (options.value === null || shownScope.value === null) {
        return null
    }

    const current = shownScope.value
    const parts: string[] = []

    for (const region of options.value.regions) {
        const country = region.countries.find((candidate) => candidate.geo === current.geo)

        if (region.geo !== current.geo && country === undefined) {
            continue
        }

        parts.push(region.label)

        if (country !== undefined && region.countries.length > 1) {
            parts.push(country.label)
        }

        break
    }

    const optionLabel = (list: Array<{ code: string; label: string }>, code: string | null) =>
        code === null ? null : (list.find((option) => option.code === code)?.label ?? null)

    parts.push(options.value.brands.find((brand) => brand.id === current.brand_id)?.name ?? '')

    for (const label of [
        optionLabel(options.value.lines, current.line),
        current.tier === 'base' ? null : optionLabel(options.value.tiers, current.tier),
        optionLabel(options.value.storages, current.storage),
        optionLabel(options.value.grades, current.grade),
    ]) {
        if (label !== null) {
            parts.push(label)
        }
    }

    return parts.filter(Boolean).join(' · ')
})

/**
 * The last valid scope stays on screen while the next one is validated, so a pick
 * doesn't blank the filters and the views for the length of a round trip.
 */
const shownScope = ref<InsightsScope | null>(null)

watch(scope, (current) => {
    if (current !== null) {
        shownScope.value = current
    }
})

/**
 * What the filter bar shows: the pick as soon as it's made. Showing the last valid
 * scope instead made a select snap back to its previous value until `/filters`
 * answered, then jump to the new one.
 */
const selection = computed<InsightsScope | null>(() => {
    if (shownScope.value === null) {
        return null
    }

    const wanted = requested.value

    return {
        geo: wanted.geo ?? shownScope.value.geo,
        brand_id: wanted.brand_id === null ? shownScope.value.brand_id : Number(wanted.brand_id),
        line: wanted.line,
        tier: wanted.tier,
        storage: wanted.storage,
        grade: wanted.grade,
    }
})

/** The scope's identity, so a view reloads only when the scope really changed. */
const scopeKey = computed(() =>
    shownScope.value === null ? null : JSON.stringify(SCOPE_KEYS.map((key) => shownScope.value![key])),
)

/**
 * The scope's summary, loaded here rather than in the By model view: its context line
 * heads the page in both views, where the other pages show their last update.
 */
const summary = ref<InsightsSummary | null>(null)
let summaryRequest = 0

const loadSummary = async () => {
    if (shownScope.value === null) {
        return
    }

    const token = ++summaryRequest

    try {
        const { data } = await insightsApi.summary(scopeQuery(shownScope.value))

        if (token === summaryRequest) {
            summary.value = data
        }
    } catch {
        if (token === summaryRequest) {
            failed.value = true
        }
    }
}

watch(scopeKey, loadSummary)

/**
 * "Based on N competitors · M listings · week of {Monday}" (ADR-021): both sides added
 * up, listings being the 7-day average every weight uses.
 */
const context = computed(() => {
    const week = options.value?.week ?? null

    if (week === null) {
        return null
    }

    const sides = INSIGHTS_SIDES.flatMap((side) => {
        const values = summary.value?.[side] ?? null

        return values === null ? [] : [values]
    })

    return {
        competitors: summary.value === null ? null : sides.reduce((total, values) => total + values.competitors, 0),
        listings: summary.value === null ? null : sides.reduce((total, values) => total + values.listings, 0),
        week: dayjs(week)
            .toDate()
            .toLocaleDateString(locale.value, { day: 'numeric', month: 'short', year: 'numeric' }),
    }
})
</script>

<template>
    <div class="-mx-6 sm:-mx-8 border-b px-6 sm:px-8 pb-4">
        <h1 class="text-3xl font-bold tracking-tight">{{ t('insights.title') }}</h1>
        <p v-if="scopeTitle" class="mt-1 text-sm text-muted-foreground">{{ scopeTitle }}</p>
    </div>

    <div class="mt-8 space-y-8">
        <p v-if="failed" class="text-sm text-destructive">{{ t('common.errors.somethingWrong') }}</p>

        <div v-else-if="options === null" class="space-y-6">
            <div class="flex flex-wrap gap-3">
                <Skeleton v-for="index in 5" :key="index" class="h-14 w-44" />
            </div>
            <Skeleton class="h-28 w-full" />
            <Skeleton class="h-80 w-full" />
        </div>

        <div
            v-else-if="hasNoData"
            class="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed px-6 py-20 text-center"
        >
            <div class="flex size-10 items-center justify-center rounded-full bg-muted">
                <ChartLine class="size-5 text-muted-foreground" />
            </div>
            <h2 class="text-lg font-semibold">{{ t('insights.empty.title') }}</h2>
            <p class="max-w-md text-sm text-muted-foreground">{{ t('insights.empty.body') }}</p>
        </div>

        <template v-else>
            <div class="flex flex-wrap items-center justify-between gap-4">
                <div v-if="context">
                    <div class="flex items-center gap-1.5 text-sm font-medium">
                        {{ t('insights.context.competitors') }}:
                        <span class="font-bold">{{ formatCount(context.competitors, locale) }}</span>
                        <span class="mx-1 text-muted-foreground">·</span>
                        {{ t('insights.context.listings') }}:
                        <span class="font-bold">{{ formatCount(context.listings, locale) }}</span>
                        <InsightsInfoTip
                            :title="t('insights.context.competitors')"
                            :text="t('insights.tips.context')"
                        />
                    </div>
                    <div class="text-xs text-muted-foreground">
                        {{ t('insights.context.week', { week: context.week }) }} · {{ t('insights.context.refresh') }}
                    </div>
                </div>
                <InsightsSegmented
                    :model-value="view"
                    :options="viewOptions"
                    class="ml-auto"
                    @update:model-value="updateQuery({ view: $event === 'model' ? null : $event })"
                />
            </div>

            <InsightsFilterBar
                v-if="selection"
                :options="options"
                :scope="selection"
                :class="{ 'pointer-events-none opacity-60': loadingFilters }"
                @change="onFilterChange"
            />

            <template v-if="shownScope && scopeKey">
                <InsightsModelView
                    v-if="view === 'model'"
                    :scope="shownScope"
                    :scope-key="scopeKey"
                    :range="range"
                    :summary="summary"
                    @update:range="updateQuery({ range: $event === '1y' ? null : $event })"
                />
                <InsightsCompetitorView v-else :scope="shownScope" :scope-key="scopeKey" />
            </template>
        </template>
    </div>
</template>
